// TenantAdminActions — a single source of truth for every per-tenant admin capability.
//
// Used in two places so the actions never drift apart:
//   • Platform Console tenant table — as a row "Actions" menu   (variant="menu")
//   • Tenant page (/platform/tenant/:id) — as a button row       (variant="buttons")
//
// It owns the modal state, so a menu item can open a dialog that outlives the menu closing.
import React, { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Menu, MenuItem, ListItemIcon, ListItemText, Button, Box,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
  CircularProgress, LinearProgress, FormControlLabel, Switch, Divider, Typography, Chip,
  FormControl, InputLabel, Select,
} from "@mui/material";
import { toast } from "react-toastify";
import GroupIcon from "@mui/icons-material/Group";
import PersonAddIcon from "@mui/icons-material/PersonAddAlt1";
import MailIcon from "@mui/icons-material/MailOutline";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ChatIcon from "@mui/icons-material/ChatBubbleOutline";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import StorageIcon from "@mui/icons-material/Storage";
import EventNoteIcon from "@mui/icons-material/EventNote";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InsightsIcon from "@mui/icons-material/Insights";
import PaidIcon from "@mui/icons-material/Paid";
import HistoryIcon from "@mui/icons-material/History";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";

import TenantUsersModal from "../TenantUsersModal";
import AddUserModal from "../AddUserModal";
import InviteUsersModal from "../InviteUsersModal";
import TenantApiKeyModal from "../TenantApiKeyModal";
import TenantSchedulerModal from "../TenantSchedulerModal";
import CustomPromptManager from "./CustomPromptManager";
import {
  updateTenant, getFinancialReportPrompt, saveFinancialReportPrompt, deleteFinancialReportPrompt, getUsersByTenant,
  getPreferencesByTenantIdAndFeature, updateDashboardPreferencesByTenantIdAndFeature,
} from "../api/tenantApi";
import { triggerPortfolioRefresh, sendTestWeeklyArSummary, sendTestCriticalAlert, rescoreArCompanies, rescoreProspectCompanies, getActionJob, sendTenantEmail } from "../api/underwritingApi";
import { markRefreshing, clearRefreshing, isRefreshing, markTenantRefreshing, clearTenantRefreshing } from "../utils/refreshRegistry";
import { useSelector } from "react-redux";
import { getDistributors, assignDistributorClient, unassignDistributorClient } from "../api/distributorsApi";
import { Autocomplete } from "@mui/material";
import HubIcon from "@mui/icons-material/Hub";
import SuretyOnboardingAdminDialog from "./SuretyOnboardingAdminDialog";
import OnboardingQuestionnaire from "../OnboardingQuestionnaire";
import { refreshAllPaymentPlatformData } from "../api/companyApi";

const ACTION_LOG_LIMIT = 100;
const actionLogKey = (tenantId) => `crediarc:tenant-action-log:${tenantId}`;

const readActionLog = (tenantId) => {
  if (!tenantId) return [];
  try {
    const parsed = JSON.parse(localStorage.getItem(actionLogKey(tenantId)) || "[]");
    return Array.isArray(parsed) ? parsed.slice(0, ACTION_LOG_LIMIT).map((entry) => {
      const isUntrackedBackgroundReceipt = ["rescoreAr", "rescoreProspects"].includes(entry?.key)
        && entry?.status === "success" && !entry?.jobId;
      return isUntrackedBackgroundReceipt ? {
        ...entry,
        status: "info",
        message: "Legacy request receipt — the backend accepted this request without live job tracking, so no progress events are available.",
      } : entry;
    }) : [];
  } catch (_) { return []; }
};

const writeActionLog = (tenantId, entries) => {
  const next = entries.slice(0, ACTION_LOG_LIMIT);
  try { localStorage.setItem(actionLogKey(tenantId), JSON.stringify(next)); } catch (_) {}
  return next;
};

const statusColor = { running: "info", success: "success", error: "error", info: "default" };

function TenantActionLogDialog({ open, onClose, tenant, entries, onClear }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <HistoryIcon color="primary" />
        <Box>
          <Typography sx={{ fontWeight: 750, lineHeight: 1.2 }}>Tenant action log</Typography>
          <Typography variant="caption" color="text.secondary">{tenant?.name || tenant?.id}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#f7f9fc", p: 2 }}>
        {entries.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center", color: "#7a8498" }}>
            <HistoryIcon sx={{ fontSize: 36, opacity: 0.45, mb: 1 }} />
            <Typography sx={{ fontWeight: 650 }}>No actions recorded yet</Typography>
            <Typography variant="body2">Run a tenant action and its complete response will appear here.</Typography>
          </Box>
        ) : entries.map((entry) => (
          <Box key={entry.id} sx={{ bgcolor: "#fff", border: "1px solid #e5eaf2", borderRadius: 2, p: 1.5, mb: 1.25 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Chip size="small" label={entry.status} color={statusColor[entry.status] || "default"} sx={{ textTransform: "capitalize", fontWeight: 700 }} />
              <Typography sx={{ fontWeight: 750, color: "#1f2a44", flex: 1 }}>{entry.action}</Typography>
              <Typography variant="caption" sx={{ color: "#7a8498", fontVariantNumeric: "tabular-nums" }}>
                {new Date(entry.timestamp).toLocaleString()}{entry.durationMs != null ? ` · ${entry.durationMs} ms` : ""}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 1, color: entry.status === "error" ? "#b3261e" : "#44516b" }}>
              {entry.message}
            </Typography>
            {entry.status === "running" && entry.progress?.total > 0 && (
              <Box sx={{ mt: 1.25 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: "#64748b", textTransform: "capitalize" }}>{entry.progress.phase}</Typography>
                  <Typography variant="caption" sx={{ color: "#64748b", fontVariantNumeric: "tabular-nums" }}>{entry.progress.current} / {entry.progress.total}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={Math.min(100, (entry.progress.current / entry.progress.total) * 100)} sx={{ height: 7, borderRadius: 4 }} />
              </Box>
            )}
            {Array.isArray(entry.events) && entry.events.length > 0 && (
              <Box sx={{ mt: 1.5, borderLeft: "2px solid #dce6f4", pl: 1.5 }}>
                {entry.events.map((event) => (
                  <Box key={event.id} sx={{ position: "relative", pb: 1.25, "&:last-child": { pb: 0 } }}>
                    <Box sx={{ position: "absolute", left: -20, top: 5, width: 9, height: 9, borderRadius: "50%", bgcolor: event.level === "error" ? "#d32f2f" : event.level === "warning" ? "#ed8b00" : event.phase === "completed-company" || event.phase === "email-sent" ? "#2e7d32" : "#1E6FE8", boxShadow: "0 0 0 3px #fff" }} />
                    <Box sx={{ display: "flex", gap: 1, alignItems: "baseline", flexWrap: "wrap" }}>
                      <Typography variant="caption" sx={{ color: "#7a8498", fontVariantNumeric: "tabular-nums" }}>{new Date(event.timestamp).toLocaleTimeString()}</Typography>
                      <Typography variant="caption" sx={{ color: "#74809a", textTransform: "uppercase", fontWeight: 750, letterSpacing: ".04em" }}>{event.phase}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: event.level === "error" ? "#b3261e" : "#334155" }}>{event.message}</Typography>
                    {event.details && !event.details.companyStatus && (
                      <Typography component="pre" variant="caption" sx={{ display: "block", m: 0, mt: 0.4, color: "#64748b", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{JSON.stringify(event.details, null, 2)}</Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
            {entry.details != null && (!entry.events || entry.events.length === 0) && (
              <Box component="pre" sx={{ mt: 1, mb: 0, p: 1.25, borderRadius: 1.5, bgcolor: "#0f172a", color: "#dbe7ff", fontSize: 11.5, lineHeight: 1.55, overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {typeof entry.details === "string" ? entry.details : JSON.stringify(entry.details, null, 2)}
              </Box>
            )}
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button color="error" onClick={onClear} disabled={!entries.length}>Clear log</Button>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ---------- self-contained modals for the tools that were only inline in AdminPage ---------- */

function FinancialReportPromptModal({ open, onClose, tenantId }) {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  React.useEffect(() => {
    if (open && tenantId) {
      setLoading(true);
      getFinancialReportPrompt(tenantId)
        .then((r) => setPrompt(r?.prompt || ""))
        .catch(() => setPrompt(""))
        .finally(() => setLoading(false));
    }
  }, [open, tenantId]);
  const save = () => {
    setLoading(true);
    saveFinancialReportPrompt(tenantId, prompt)
      .then(() => { toast.success("Prompt saved"); onClose(); })
      .catch(() => toast.error("Failed to save prompt"))
      .finally(() => setLoading(false));
  };
  const clear = () => {
    setLoading(true);
    deleteFinancialReportPrompt(tenantId)
      .then(() => { setPrompt(""); toast.success("Restored to default"); })
      .catch(() => toast.error("Failed to restore default"))
      .finally(() => setLoading(false));
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Financial Report Prompt</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Variables: <strong>#company_name</strong>, <strong>#ticker</strong>, <strong>#country</strong>.
        </Alert>
        {loading ? <CircularProgress /> : (
          <TextField multiline rows={14} fullWidth value={prompt}
            onChange={(e) => setPrompt(e.target.value)} variant="outlined" />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={clear} color="secondary" disabled={loading}>Clear</Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={save} variant="contained" disabled={loading}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

function DNBServiceModal({ open, onClose, tenant }) {
  const tenantId = tenant?.id;
  const [search, setSearch] = useState(!!tenant?.isDNBSearchEnabled);
  const [fin, setFin] = useState(!!tenant?.isDNBFinancialDataEnabled);
  const [busy, setBusy] = useState(false);
  React.useEffect(() => {
    setSearch(!!tenant?.isDNBSearchEnabled);
    setFin(!!tenant?.isDNBFinancialDataEnabled);
  }, [tenant, open]);
  const persist = async (field, value, setter) => {
    setter(value); setBusy(true);
    try {
      await updateTenant(tenantId, { [field]: value });
      toast.success(`DNB ${field === "isDNBSearchEnabled" ? "search" : "financial data"} ${value ? "enabled" : "disabled"}`);
    } catch (e) {
      setter(!value);
      toast.error("Update failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>D&amp;B Services</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 1 }}>
        <FormControlLabel control={<Switch checked={search} disabled={busy}
          onChange={(e) => persist("isDNBSearchEnabled", e.target.checked, setSearch)} />} label="Enable D&B Search" />
        <FormControlLabel control={<Switch checked={fin} disabled={busy}
          onChange={(e) => persist("isDNBFinancialDataEnabled", e.target.checked, setFin)} />} label="Enable D&B Financial Data API" />
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function CompanyAllowanceDialog({ open, onClose, tenant, onChanged }) {
  const [allowance, setAllowance] = useState("");
  const [contractLimit, setContractLimit] = useState("");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    setAllowance(String(Number(tenant?.counter) || 0));
    setContractLimit(String(Number(tenant?.maxCounterLimit) || 0));
  }, [open, tenant]);

  const parsedAllowance = Number(allowance);
  const parsedLimit = Number(contractLimit);
  const valid = allowance.trim() !== "" && contractLimit.trim() !== ""
    && Number.isInteger(parsedAllowance) && parsedAllowance >= 0
    && Number.isInteger(parsedLimit) && parsedLimit >= 0
    && (parsedLimit === 0 || parsedAllowance <= parsedLimit);

  const save = async () => {
    if (!valid || !tenant?.id) return;
    setSaving(true);
    try {
      const response = await updateTenant(tenant.id, {
        counter: parsedAllowance,
        maxCounterLimit: parsedLimit,
      });
      const updated = response?.data || {
        ...tenant,
        counter: parsedAllowance,
        maxCounterLimit: parsedLimit,
      };
      onChanged({ ...tenant, ...updated, id: tenant.id });
      toast.success(`Company allowance updated to ${parsedAllowance.toLocaleString()}`);
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Couldn't update the company allowance");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 750 }}>Company allowance — {tenant?.name || "tenant"}</DialogTitle>
      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
        <Alert severity="info">
          The available-companies value is the counter shown to the tenant when they add companies. It decreases as companies are added.
        </Alert>
        <TextField
          autoFocus
          fullWidth
          size="small"
          type="number"
          label="Additional companies available"
          value={allowance}
          onChange={(event) => setAllowance(event.target.value)}
          inputProps={{ min: 0, step: 1 }}
          error={allowance !== "" && (!Number.isInteger(parsedAllowance) || parsedAllowance < 0 || (parsedLimit > 0 && parsedAllowance > parsedLimit))}
          helperText={parsedLimit > 0 ? `Must be a whole number from 0 to ${parsedLimit.toLocaleString()}.` : "Enter a non-negative whole number."}
        />
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Contract company limit"
          value={contractLimit}
          onChange={(event) => setContractLimit(event.target.value)}
          inputProps={{ min: 0, step: 1 }}
          error={contractLimit !== "" && (!Number.isInteger(parsedLimit) || parsedLimit < 0)}
          helperText="Use 0 when the contract does not have a fixed limit."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={save} variant="contained" disabled={saving || !valid}
          startIcon={saving ? <CircularProgress size={16} /> : <AddCircleOutlineIcon />}>
          {saving ? "Saving…" : "Save allowance"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AgingDaysModal({ open, onClose, tenantId }) {
  const [days, setDays] = useState({ age1: "", age2: "", age3: "" });
  const [loading, setLoading] = useState(false);
  const strip = (v) => (v == null ? "" : String(v).replace(/[^0-9]/g, ""));
  React.useEffect(() => {
    if (open && tenantId) {
      setLoading(true);
      getPreferencesByTenantIdAndFeature(tenantId, "agingDays")
        .then((r) => {
          const p = r?.data?.preferences;
          if (p) setDays({ age1: strip(p.age1), age2: strip(p.age2), age3: strip(p.age3) });
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open, tenantId]);
  const set = (k) => (e) => setDays({ ...days, [k]: e.target.value.replace(/[^0-9]/g, "") });
  const save = async () => {
    setLoading(true);
    try {
      await updateDashboardPreferencesByTenantIdAndFeature(tenantId, "agingDays", {
        preferences: { age1: days.age1, age2: days.age2, age3: days.age3 },
      });
      toast.success("Aging days saved");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save aging days");
    } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Aging Days</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        {loading ? <CircularProgress /> : (
          <>
            <TextField label="Aging bucket 1 (days)" value={days.age1} onChange={set("age1")} size="small" />
            <TextField label="Aging bucket 2 (days)" value={days.age2} onChange={set("age2")} size="small" />
            <TextField label="Aging bucket 3 (days)" value={days.age3} onChange={set("age3")} size="small" />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={save} variant="contained" disabled={loading}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

function AssignDistributorModal({ open, onClose, tenant }) {
  const tenantId = tenant?.id;
  const [dists, setDists] = useState(null);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  React.useEffect(() => {
    if (!open) return;
    setPick(null);
    getDistributors().then((r) => setDists(r?.data || [])).catch(() => setDists([]));
  }, [open]);
  const current = (dists || []).find((d) => (d.clientTenantIds || []).includes(tenantId)) || null;
  const assign = async () => {
    if (!pick?.id) return;
    setBusy(true);
    try {
      if (current && current.id !== pick.id) await unassignDistributorClient(current.id, tenantId).catch(() => {});
      await assignDistributorClient(pick.id, tenantId);
      toast.success(`Assigned to ${pick.name}`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.error || "Assign failed"); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    if (!current) { onClose(); return; }
    setBusy(true);
    try { await unassignDistributorClient(current.id, tenantId); toast.success("Unassigned"); onClose(); }
    catch (e) { toast.error("Failed to unassign"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Assign to distributor</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        {dists === null ? <CircularProgress size={22} /> : (
          <>
            <Box sx={{ fontSize: 13, color: "#64748b" }}>
              Current: <b>{current ? current.name : "— none —"}</b>
            </Box>
            <Autocomplete size="small" options={dists} value={pick}
              getOptionLabel={(o) => `${o?.name || ""}${o?.type ? ` (${o.type})` : ""}`}
              onChange={(_, v) => setPick(v)}
              renderInput={(p) => <TextField {...p} label="Distributor" />} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {current && <Button color="secondary" onClick={clear} disabled={busy}>Unassign</Button>}
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={assign} variant="contained" disabled={busy || !pick}>Assign</Button>
      </DialogActions>
    </Dialog>
  );
}

function TenantEmailModal({ open, onClose, tenant, onSend, busy }) {
  const [type, setType] = useState("ar-report");
  const [recipientMode, setRecipientMode] = useState("all-users");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  React.useEffect(() => {
    if (!open) return;
    setType("ar-report");
    setRecipientMode("all-users");
    setEmail("");
    setSubject("");
    setMessage("");
  }, [open]);

  const canSend = recipientMode === "all-users" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const newsletterReady = type !== "newsletter" || (subject.trim() && message.trim());
  const submit = async () => {
    const result = await onSend({ type, recipientMode, email: email.trim(), subject: subject.trim(), message: message.trim() });
    if (result) onClose();
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 750 }}>Send email — {tenant?.name || "tenant"}</DialogTitle>
      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
        <Alert severity="info">AR reports use the current saved portfolio. Sending from here does not refresh or re-underwrite any company.</Alert>
        <FormControl fullWidth size="small">
          <InputLabel>Email type</InputLabel>
          <Select label="Email type" value={type} onChange={(event) => setType(event.target.value)}>
            <MenuItem value="ar-report">AR report</MenuItem>
            <MenuItem value="newsletter">Newsletter</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel>Recipients</InputLabel>
          <Select label="Recipients" value={recipientMode} onChange={(event) => setRecipientMode(event.target.value)}>
            <MenuItem value="all-users">All tenant users</MenuItem>
            <MenuItem value="specific">Specific email address</MenuItem>
          </Select>
        </FormControl>
        {recipientMode === "specific" && (
          <TextField fullWidth size="small" type="email" label="Email address" value={email} onChange={(event) => setEmail(event.target.value)} error={!!email && !canSend} />
        )}
        {type === "newsletter" && (
          <>
            <TextField fullWidth size="small" label="Newsletter subject" value={subject} onChange={(event) => setSubject(event.target.value)} inputProps={{ maxLength: 150 }} />
            <TextField fullWidth multiline minRows={7} label="Newsletter message" value={message} onChange={(event) => setMessage(event.target.value)} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} variant="contained" disabled={busy || !canSend || !newsletterReady} startIcon={busy ? <CircularProgress size={16} /> : <MailIcon />}>
          {busy ? "Sending…" : "Send email"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ---------------------------------- the hub ---------------------------------- */

export default function TenantAdminActions({
  tenant, variant = "menu", anchorEl = null, open = false, onClose = () => {}, onChanged = () => {},
}) {
  const navigate = useNavigate();
  const [active, setActive] = useState(null); // which modal is open
  const [busy, setBusy] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [actionLog, setActionLog] = useState([]);
  const pollTimers = useRef(new Map());
  const adminEmail = useSelector((s) => s.userInfo.email);
  const tid = tenant?.id;

  React.useEffect(() => { setActionLog(readActionLog(tid)); }, [tid]);

  const persistLog = useCallback((updater) => {
    const next = writeActionLog(tid, updater(readActionLog(tid)));
    setActionLog(next);
    return next;
  }, [tid]);

  const applyCompanyEvents = (events = []) => {
    const latest = new Map();
    events.forEach((event) => {
      const companyId = event?.details?.companyId;
      const companyStatus = event?.details?.companyStatus;
      if (companyId && companyStatus) latest.set(companyId, companyStatus);
    });
    latest.forEach((status, companyId) => {
      if (status === "refreshing" && !isRefreshing(companyId)) markRefreshing(companyId);
      if (status === "finished" || status === "failed") clearRefreshing(companyId);
    });
  };

  const pollActionJob = useCallback(async (entryId, jobId) => {
    try {
      const response = await getActionJob(jobId);
      const job = response?.job;
      if (!job) throw new Error("Background job was not found");
      applyCompanyEvents(job.events);
      const lastEvent = Array.isArray(job.events) ? job.events[job.events.length - 1] : null;
      persistLog((current) => current.map((entry) => entry.id === entryId ? {
        ...entry,
        status: job.status || "running",
        message: lastEvent?.message || entry.message,
        events: job.events || [],
        progress: job.progress || null,
        details: job.status === "running" ? null : (job.result || job.error || null),
        durationMs: job.completedAt ? new Date(job.completedAt).getTime() - new Date(entry.timestamp).getTime() : undefined,
      } : entry));
      if (job.status === "running") {
        const timer = setTimeout(() => pollActionJob(entryId, jobId), 2500);
        pollTimers.current.set(entryId, timer);
      } else {
        clearTenantRefreshing(job.tenantId);
        pollTimers.current.delete(entryId);
      }
    } catch (error) {
      // A transient network failure must not turn a healthy backend job into a failed one.
      persistLog((current) => current.map((entry) => entry.id === entryId ? {
        ...entry, message: `Waiting for live progress… (${error?.message || "connection error"})`,
      } : entry));
      const timer = setTimeout(() => pollActionJob(entryId, jobId), 5000);
      pollTimers.current.set(entryId, timer);
    }
  }, [persistLog]);

  React.useEffect(() => {
    actionLog.forEach((entry) => {
      if (entry.status === "running" && entry.jobId && !pollTimers.current.has(entry.id)) {
        pollTimers.current.set(entry.id, true);
        pollActionJob(entry.id, entry.jobId);
      }
    });
  }, [actionLog, pollActionJob]);

  React.useEffect(() => () => {
    pollTimers.current.forEach((timer) => { if (timer !== true) clearTimeout(timer); });
    pollTimers.current.clear();
  }, [tid]);

  const executeAction = async ({ key, label, run, showLog = false }) => {
    const id = `${Date.now()}-${key}-${Math.random().toString(36).slice(2, 7)}`;
    const started = Date.now();
    persistLog((current) => [{ id, key, action: label, status: "running", message: "Action started", timestamp: new Date(started).toISOString() }, ...current]);
    if (showLog) setLogOpen(true);
    try {
      const result = await run();
      const jobId = result?.details?.jobId;
      if (jobId) {
        persistLog((current) => current.map((entry) => entry.id === id ? {
          ...entry, jobId, status: "running",
          message: result?.logMessage || "Background job started",
          details: null,
        } : entry));
        pollTimers.current.set(id, true);
        pollActionJob(id, jobId);
        return result;
      }
      persistLog((current) => current.map((entry) => entry.id === id ? {
        ...entry, status: "success", message: result?.logMessage || "Action completed successfully",
        details: result?.details !== undefined ? result.details : null,
        durationMs: Date.now() - started,
      } : entry));
      return result;
    } catch (error) {
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || "Action failed";
      persistLog((current) => current.map((entry) => entry.id === id ? {
        ...entry, status: "error", message, durationMs: Date.now() - started,
        details: { status: error?.response?.status, response: error?.response?.data || null },
      } : entry));
      return null;
    }
  };

  const clearActionLog = () => {
    pollTimers.current.forEach((timer) => { if (timer !== true) clearTimeout(timer); });
    pollTimers.current.clear();
    setActionLog([]);
    try { localStorage.removeItem(actionLogKey(tid)); } catch (_) {}
  };

  const closeModal = () => setActive(null);
  const actionError = (error, fallback) => {
    const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback;
    toast.error(message);
    throw error instanceof Error ? error : new Error(message);
  };

  const rescore = async () => {
    if (!tid) throw new Error("Missing tenant ID");
    try {
      const response = await triggerPortfolioRefresh({ tenants: [tid], scoreOnly: true });
      const message = `Re-scoring all companies for ${tenant?.name || "tenant"} — running in the background`;
      toast.success(message);
      return { logMessage: message, details: response };
    } catch (e) {
      return actionError(e, "Couldn't start re-score");
    }
  };

  const refreshAllMcaData = async () => {
    if (!tid) throw new Error("Missing tenant ID");
    setBusy(true);
    try {
      const response = await refreshAllPaymentPlatformData(tid);
      const fileCount = Number(response?.totalFiles || 0);
      const message = `Refreshing all MCA data for ${tenant?.name || "tenant"} from ${fileCount} file${fileCount === 1 ? "" : "s"}. Matching and assessments are running in the background.`;
      toast.success(message);
      return { logMessage: message, details: response };
    } catch (e) {
      return actionError(e, "Couldn't refresh all MCA data");
    } finally { setBusy(false); }
  };

  // Refresh ONLY the ARC scores across all prospects + AR customers (score-only — no expensive AI
  // summaries/enrichment). Any customer crossing from <8 into ≥8 fires a high-risk notification email
  // (with explainability) via the underwriting flow's crossing-alert path.
  const refreshScoresAndAlert = async () => {
    if (!tid) throw new Error("Missing tenant ID");
    setBusy(true);
    try {
      const response = await triggerPortfolioRefresh({ tenants: [tid], scoreOnly: true });
      const message = `Refreshing ARC scores for all prospects & AR customers of ${tenant?.name || "tenant"}. Anyone crossing into high-risk (ARC ≥ 8) gets a notification email. Running in the background.`;
      toast.success(message);
      return { logMessage: message, details: response };
    } catch (e) {
      return actionError(e, "Couldn't start the refresh");
    } finally { setBusy(false); }
  };

  // Admin self-tests: email me a preview of the weekly AR summary / the high-risk alert for this tenant.
  const testArSummary = async () => {
    if (!tid) throw new Error("Missing tenant ID");
    if (!adminEmail) return actionError(new Error("No admin email on your account."), "No admin email on your account.");
    setBusy(true);
    try {
      const response = await sendTestWeeklyArSummary({ tenantId: tid, testEmail: adminEmail, refresh: false });
      const message = `Test AR summary for ${tenant?.name || "tenant"} sent to ${adminEmail}`;
      toast.success(message);
      return { logMessage: message, details: response };
    } catch (e) {
      return actionError(e, "Couldn't send test summary");
    } finally { setBusy(false); }
  };

  // Rescore ONLY the AR customers (score-only), then email me a before/after score-delta report.
  const rescoreAr = async () => {
    if (!tid) throw new Error("Missing tenant ID");
    if (!adminEmail) return actionError(new Error("No admin email on your account."), "No admin email on your account.");
    setBusy(true);
    markTenantRefreshing(tid);
    try {
      const response = await rescoreArCompanies({ tenantId: tid, tenantName: tenant?.name, notifyEmail: adminEmail });
      if (!response?.jobId) throw new Error("The backend did not return a live job ID. Please wait for the backend deployment before retrying.");
      const message = `Rescoring AR customers of ${tenant?.name || "tenant"} — a score-change report will be emailed to ${adminEmail} when it finishes.`;
      toast.success(message);
      return { logMessage: message, details: response };
    } catch (e) {
      clearTenantRefreshing(tid);
      return actionError(e, "Couldn't start the AR rescore");
    } finally { setBusy(false); }
  };

  // Rescore ONLY the prospects (non-AR companies, score-only), then email me a before/after delta report.
  const rescoreProspects = async () => {
    if (!tid) throw new Error("Missing tenant ID");
    if (!adminEmail) return actionError(new Error("No admin email on your account."), "No admin email on your account.");
    setBusy(true);
    markTenantRefreshing(tid);
    try {
      const response = await rescoreProspectCompanies({ tenantId: tid, tenantName: tenant?.name, notifyEmail: adminEmail });
      if (!response?.jobId) throw new Error("The backend did not return a live job ID. Please wait for the backend deployment before retrying.");
      const message = `Rescoring prospects of ${tenant?.name || "tenant"} — a score-change report will be emailed to ${adminEmail} when it finishes.`;
      toast.success(message);
      return { logMessage: message, details: response };
    } catch (e) {
      clearTenantRefreshing(tid);
      return actionError(e, "Couldn't start the prospect rescore");
    } finally { setBusy(false); }
  };

  const testAlert = async () => {
    if (!adminEmail) return actionError(new Error("No admin email on your account."), "No admin email on your account.");
    setBusy(true);
    try {
      const response = await sendTestCriticalAlert({ testEmail: adminEmail, companyName: `${tenant?.name || "A"} customer` });
      const message = `Test high-risk alert sent to ${adminEmail}`;
      toast.success(message);
      return { logMessage: message, details: response };
    } catch (e) {
      return actionError(e, "Couldn't send test alert");
    } finally { setBusy(false); }
  };

  const sendManualTenantEmail = async ({ type, recipientMode, email, subject, message }) => {
    if (!tid) throw new Error("Missing tenant ID");
    setBusy(true);
    try {
      let recipients;
      if (recipientMode === "specific") {
        recipients = [email];
      } else {
        const response = await getUsersByTenant(tid);
        const users = response?.data?.users || response?.users || response?.data || [];
        recipients = [...new Set((Array.isArray(users) ? users : []).map((user) => user?.email).filter(Boolean))];
        if (!recipients.length) throw new Error("This tenant has no users with an email address");
      }
      const response = await sendTenantEmail({ tenantId: tid, tenantName: tenant?.name, type, recipients, subject, message });
      const label = type === "ar-report" ? "AR report" : "Newsletter";
      const resultMessage = response?.message || `${label} sent to ${recipients.length} recipient${recipients.length === 1 ? "" : "s"}`;
      toast.success(resultMessage);
      return { logMessage: resultMessage, details: { ...response, recipients } };
    } catch (e) {
      return actionError(e, "Couldn't send the tenant email");
    } finally { setBusy(false); }
  };

  const togglePaid = async () => {
    if (!tid) throw new Error("Missing tenant ID");
    setBusy(true);
    try {
      const response = await updateTenant(tid, { isPaid: !tenant.isPaid });
      const message = !tenant.isPaid ? "Marked as paid" : "Moved to trial";
      toast.success(message);
      onChanged({ ...tenant, isPaid: !tenant.isPaid });
      return { logMessage: message, details: response };
    } catch (e) {
      return actionError(e, "Update failed");
    } finally { setBusy(false); }
  };

  // One-click grant for the tenant's Accounts Receivable subscription. The Plan & features
  // switch remains available for deliberate removal; this action is intentionally grant-only
  // so an admin cannot disable a customer's AR access by clicking the primary permission button
  // a second time.
  const enableArSubscription = async () => {
    if (!tid) throw new Error("Missing tenant ID");
    if (tenant?.isAREnabled) {
      return { logMessage: "Accounts Receivable subscription is already enabled", details: { isAREnabled: true } };
    }
    setBusy(true);
    try {
      const response = await updateTenant(tid, { isAREnabled: true });
      const message = `Accounts Receivable subscription enabled for ${tenant?.name || "tenant"}`;
      toast.success(message);
      onChanged({ ...tenant, isAREnabled: true });
      return { logMessage: message, details: response };
    } catch (e) {
      return actionError(e, "Couldn't enable the Accounts Receivable subscription");
    } finally { setBusy(false); }
  };

  const openTool = (tool, label) => {
    setActive(tool);
    return { logMessage: `${label} opened` };
  };
  const openPage = (path, label) => {
    navigate(path);
    return { logMessage: `${label} opened`, details: { path } };
  };
  const openPageInNewTab = (path, label) => {
    window.open(path, "_blank", "noopener,noreferrer");
    return { logMessage: `${label} opened in a new tab`, details: { path, target: "_blank" } };
  };

  // Full per-tenant admin capability set (order = grouped: people, config, data, run).
  const actions = [
    { key: "users", label: "Manage users", Icon: GroupIcon, run: () => openTool("users", "Manage users") },
    { key: "addUser", label: "Add user", Icon: PersonAddIcon, run: () => openTool("addUser", "Add user") },
    { key: "invite", label: "Invite users", Icon: MailIcon, run: () => openTool("invite", "Invite users") },
    { key: "clientOnboarding", label: "Client onboarding", Icon: FactCheckOutlinedIcon, run: () => openTool("clientOnboarding", "Client onboarding") },
    { key: "suretyOnboarding", label: "Surety onboarding", Icon: FactCheckOutlinedIcon, run: () => openTool("suretyOnboarding", "Surety onboarding") },
    { key: "sendEmail", label: "Send email", Icon: MailIcon, run: () => openTool("sendEmail", "Send email") },
    { key: "apiKey", label: "API keys", Icon: VpnKeyIcon, run: () => openTool("apiKey", "API keys") },
    { key: "scheduler", label: "Scheduler", Icon: ScheduleIcon, run: () => openTool("scheduler", "Scheduler") },
    { key: "customPrompt", label: "Custom prompts", Icon: ChatIcon, run: () => openTool("customPrompt", "Custom prompts") },
    { key: "finPrompt", label: "Financial report prompt", Icon: DescriptionIcon, run: () => openTool("finPrompt", "Financial report prompt") },
    { key: "dnb", label: "D&B services", Icon: StorageIcon, run: () => openTool("dnb", "D&B services") },
    { key: "aging", label: "Aging days", Icon: EventNoteIcon, run: () => openTool("aging", "Aging days") },
    { key: "companyAllowance", label: "Company allowance", Icon: AddCircleOutlineIcon, run: () => openTool("companyAllowance", "Company allowance") },
    { key: "distributor", label: "Assign to distributor", Icon: HubIcon, run: () => openTool("distributor", "Assign to distributor") },
    { key: "dashboard", label: "Open dashboard", Icon: OpenInNewIcon, run: () => openPageInNewTab(`/tenant-dashboard/${tid}`, "Dashboard") },
    { key: "reporting", label: "Open BI & reporting", Icon: InsightsIcon, run: () => openPage(`/tenant-dashboard/${tid}/reporting`, "BI & reporting") },
    ...(tenant?.tenantType === "payment_platform" ? [{
      key: "refreshAllMca",
      label: "Refresh all MCA data",
      Icon: RefreshIcon,
      run: refreshAllMcaData,
      showLog: true,
      confirm: true,
    }] : []),
    { key: "rescore", label: "Re-score all companies", Icon: RefreshIcon, run: rescore, showLog: true },
    { key: "refreshScores", label: "Refresh ARC scores + high-risk alerts", Icon: RefreshIcon, run: refreshScoresAndAlert, showLog: true },
    { key: "rescoreAr", label: "Rescore AR companies + email delta", Icon: RefreshIcon, run: rescoreAr, showLog: true },
    { key: "rescoreProspects", label: "Rescore prospects + email delta", Icon: RefreshIcon, run: rescoreProspects, showLog: true },
    { key: "testArSummary", label: "Email me a test AR summary", Icon: MailIcon, run: testArSummary, showLog: true },
    { key: "testAlert", label: "Email me a test high-risk alert", Icon: MailIcon, run: testAlert, showLog: true },
    {
      key: "enableAr",
      label: tenant?.isAREnabled ? "AR subscription enabled" : "Enable AR subscription",
      Icon: LockOpenIcon,
      run: enableArSubscription,
      showLog: true,
      disabled: !!tenant?.isAREnabled,
    },
    { key: "paid", label: tenant?.isPaid ? "Move to trial" : "Mark as paid", Icon: PaidIcon, run: togglePaid, showLog: true },
  ];

  const modals = tid ? (
    <>
      <TenantUsersModal open={active === "users"} onClose={closeModal} tenant={tenant} />
      <AddUserModal open={active === "addUser"} onClose={closeModal} tenantInfo={tenant} />
      <InviteUsersModal open={active === "invite"} onClose={closeModal} tenant={tenant} />
      {active === "clientOnboarding" && <OnboardingQuestionnaire adminTenantId={tid} onClose={closeModal} />}
      <SuretyOnboardingAdminDialog open={active === "suretyOnboarding"} onClose={closeModal} tenant={tenant} />
      <TenantApiKeyModal open={active === "apiKey"} onClose={closeModal} tenantId={tid} />
      <TenantSchedulerModal open={active === "scheduler"} onClose={closeModal} tenantId={tid} />
      <CustomPromptManager open={active === "customPrompt"} onClose={closeModal} tenantId={tid} />
      <FinancialReportPromptModal open={active === "finPrompt"} onClose={closeModal} tenantId={tid} />
      <DNBServiceModal open={active === "dnb"} onClose={closeModal} tenant={tenant} />
      <AgingDaysModal open={active === "aging"} onClose={closeModal} tenantId={tid} />
      <CompanyAllowanceDialog open={active === "companyAllowance"} onClose={closeModal} tenant={tenant} onChanged={onChanged} />
      <AssignDistributorModal open={active === "distributor"} onClose={closeModal} tenant={tenant} />
      <TenantEmailModal
        open={active === "sendEmail"}
        onClose={closeModal}
        tenant={tenant}
        busy={busy}
        onSend={(payload) => executeAction({
          key: "sendTenantEmail",
          label: `Send ${payload.type === "ar-report" ? "AR report" : "newsletter"}`,
          run: () => sendManualTenantEmail(payload),
          showLog: true,
        })}
      />
      <Dialog open={active === "refreshAllMca"} onClose={busy ? undefined : closeModal} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>Refresh all MCA data?</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ color: "#334155", lineHeight: 1.6 }}>
            This will rescan the connected tenant bucket, include every active file in the document center,
            and rebuild entity matching and MCA assessments using the saved column mapping.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            Existing files are not deleted. The refresh is queued in the background, and updated matching and assessment results appear when processing completes.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={busy}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setActive(null);
              executeAction({
                key: "refreshAllMca",
                label: "Refresh all MCA data",
                run: refreshAllMcaData,
                showLog: true,
              });
            }}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          >
            Refresh all data
          </Button>
        </DialogActions>
      </Dialog>
      <TenantActionLogDialog open={logOpen} onClose={() => setLogOpen(false)} tenant={tenant} entries={actionLog} onClear={clearActionLog} />
    </>
  ) : null;

  if (variant === "buttons") {
    // The tenant page already provides first-class Team members and Dashboard/BI
    // controls. Avoid repeating those shortcuts in the admin tool area, and group
    // the remaining operations by the job an administrator is trying to complete.
    const buttonGroups = [
      {
        title: "Onboarding & communication",
        description: "Start journeys, communicate, and connect account ownership.",
        keys: ["clientOnboarding", "suretyOnboarding", "sendEmail", "distributor"],
      },
      {
        title: "Account configuration",
        description: "Configure integrations, schedules, prompts, and limits.",
        keys: ["apiKey", "scheduler", "customPrompt", "finPrompt", "dnb", "aging", "companyAllowance"],
      },
      {
        title: "Maintenance & notifications",
        description: "Run data maintenance or validate customer notifications.",
        keys: ["refreshAllMca", "rescore", "refreshScores", "rescoreAr", "rescoreProspects", "testArSummary", "testAlert", "enableAr", "paid"],
      },
    ];
    const actionByKey = Object.fromEntries(actions.map((action) => [action.key, action]));

    return (
      <Box data-track-area="Tenant administration tools" sx={{ display: "grid", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>
            User access is managed in the Team members section below.
          </Typography>
          <Button onClick={() => setLogOpen(true)} size="small" variant="outlined"
            aria-label={`Open tenant action log${actionLog.length ? ` with ${actionLog.length} entries` : ""}`}
            startIcon={<HistoryIcon sx={{ fontSize: "16px !important" }} />}
            sx={{ textTransform: "none", borderColor: "#bfd1ef", borderRadius: "9px", color: "#1E5DB8", fontWeight: 700 }}>
            Action log{actionLog.length ? ` (${actionLog.length})` : ""}
          </Button>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
          {buttonGroups.map((group) => (
            <Box key={group.title} component="section" aria-label={group.title} data-track-area={`${group.title} admin actions`}
              sx={{ border: "1px solid #e3ebf3", borderRadius: "13px", p: 1.5, background: "#fbfcfe" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#0B2A4A" }}>{group.title}</Typography>
              <Typography sx={{ minHeight: { lg: 34 }, fontSize: 11.5, color: "#718096", lineHeight: 1.45, mt: .25, mb: 1.25 }}>{group.description}</Typography>
              <Box sx={{ display: "grid", gap: .65 }}>
                {group.keys.map((key) => actionByKey[key]).filter(Boolean).map(({ key, label, Icon, run, showLog, disabled, confirm }) => (
                  <Button key={key} onClick={() => confirm ? setActive(key) : executeAction({ key, label, run, showLog })} disabled={busy || disabled}
                    size="small" variant="outlined" startIcon={<Icon sx={{ fontSize: "16px !important" }} />}
                    sx={{ justifyContent: "flex-start", minHeight: 36, px: 1.15, textTransform: "none", textAlign: "left",
                      borderColor: "#dde6f0", borderRadius: "8px", background: "#fff", color: "#2F3D63", fontSize: 12,
                      fontWeight: 650, lineHeight: 1.25, "&:hover": { borderColor: "#9db9e8", background: "#f5f9ff" } }}>
                    {label}
                  </Button>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
        {modals}
      </Box>
    );
  }

  // menu variant (table row)
  return (
    <>
      <Menu anchorEl={anchorEl} open={open} onClose={onClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
        <MenuItem onClick={() => { onClose(); navigate(`/platform/tenant/${tid}`); }}>
          <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Open tenant page</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { onClose(); setLogOpen(true); }}>
          <ListItemIcon><HistoryIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText primary="Action log" secondary={actionLog.length ? `${actionLog.length} recorded` : "No actions yet"} />
        </MenuItem>
        <Divider />
        {actions.map(({ key, label, Icon, run, showLog, disabled, confirm }) => (
          <MenuItem key={key} disabled={busy || disabled} onClick={() => { onClose(); confirm ? setActive(key) : executeAction({ key, label, run, showLog }); }}>
            <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
            <ListItemText>{label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
      {modals}
    </>
  );
}
